import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { ImportForm } from "~/components/import/import-form";
import { ImportHistoryTable, ImportHistorySkeleton } from "~/components/import/import-history-table";
import { useProjectContext } from "~/components/layout/project-context";
import { importBatchesQueryOptions } from "~/lib/imports/queries";

const importSearchSchema = z.object({
  tab: z.enum(["new", "history"]).default("new"),
});

export const Route = createFileRoute("/_auth/projects/$projectId/import")({
  validateSearch: importSearchSchema,
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      importBatchesQueryOptions(params.projectId),
    );
  },
  component: ImportCitationsPage,
});

function ImportCitationsPage() {
  const { projectId, projectTitle } = useProjectContext();
  const { tab } = Route.useSearch();

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1 p-6">
        <div className="animate-fade-in-up">
          <div className="mb-6">
            <h2 className="text-lg font-semibold">Import Citations</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Import citations into your project from RIS files.
            </p>
          </div>

          <div className="mb-6 flex gap-1 border-b">
            <Link
              to="/projects/$projectId/import"
              params={{ projectId }}
              search={{ tab: "new" }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tab === "new"
                  ? "border-foreground text-foreground border-b-2"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              New Import
            </Link>
            <Link
              to="/projects/$projectId/import"
              params={{ projectId }}
              search={{ tab: "history" }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tab === "history"
                  ? "border-foreground text-foreground border-b-2"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              History
            </Link>
          </div>

          {tab === "new" && <ImportForm projectId={projectId} />}
          {tab === "history" && (
            <Suspense fallback={<ImportHistorySkeleton />}>
              <ImportHistoryTable projectId={projectId} />
            </Suspense>
          )}
        </div>
      </main>
    </div>
  );
}
